import countries from 'i18n-iso-countries';
import type { i18n } from 'i18next';
import i18nInstance from 'i18next';
import moment from 'moment';
import 'moment/locale/de-ch';
import 'moment/locale/en-gb';
import 'moment/locale/fr-ch';
import 'moment/locale/it-ch';
import { initReactI18next } from 'react-i18next';
import { loadLanguage, persistLanguage } from './app/api/services/base-services/language-persistence-service';
import defaultTranslationsDe from './app/static/tenants/default/translations/de.json';
import defaultTranslationsEn from './app/static/tenants/default/translations/en.json';
import defaultTranslationsFr from './app/static/tenants/default/translations/fr.json';
import defaultTranslationsIt from './app/static/tenants/default/translations/it.json';

const LANGUAGE_QUERY_PARAM_NAME = 'lang';
const languagePathRegex = /\/(de|fr|it|en)/i;

/**
 * updates pathname in the address bar, so that the current language query parameter "lang" is removed
 * and the language path parameter (e.g. "de" for portal/de/xyz) is replaced with the query param value (if available)
 */
function updatePathNameWithLanguage(): string | undefined {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const paramLanguageCode = urlSearchParams.get(LANGUAGE_QUERY_PARAM_NAME)?.toLowerCase();

    urlSearchParams.delete(LANGUAGE_QUERY_PARAM_NAME);

    const queryString = urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : urlSearchParams.toString();
    let updatedPathname = `${window.location.pathname}${queryString}`;
    if (paramLanguageCode) {
        updatedPathname = updatedPathname.replace(languagePathRegex, `/${paramLanguageCode}`);
    }

    window.history.replaceState(null, '', updatedPathname);

    return paramLanguageCode;
}

// this is the language code contained in the route, e.g. "de" for "portal/de/?lang=fr"
const routeLanguageCode = languagePathRegex.exec(window.location.pathname)?.[1];
const storedLanguage = loadLanguage();

// this is the language code contained in the language query param (or null if not defined), e.g. "fr" for "portal/de/?lang=fr"
const paramLanguageCode = updatePathNameWithLanguage();

const initLanguage = paramLanguageCode || routeLanguageCode || storedLanguage || 'de';
persistLanguage(initLanguage);

export const initI18n = () =>
    i18nInstance.use(initReactI18next).init(
        {
            lng: initLanguage,
            fallbackLng: 'de',
            resources: {},
            keySeparator: false, // we do not use keys in form messages.welcome
            nsSeparator: '::',
            interpolation: {
                escapeValue: false, // react already safes from xss
            },
            react: {
                bindI18nStore: false,
            },
            debug: process.env.NODE_ENV === 'development',
        },
        () => {
            i18nInstance.addResourceBundle('de', 'translation', defaultTranslationsDe);
            i18nInstance.addResourceBundle('en', 'translation', defaultTranslationsEn);
            i18nInstance.addResourceBundle('fr', 'translation', defaultTranslationsFr);
            i18nInstance.addResourceBundle('it', 'translation', defaultTranslationsIt);
        },
    );

function updatePageTitle() {
    document.title = i18nInstance.t('header.tabTitle', '');
}

function updateMomentI18n() {
    moment.locale(
        {
            de: 'de-ch',
            en: 'en-gb',
            fr: 'fr-ch',
            it: 'it-ch',
        }[initLanguage],
    );
}
function importCountryTranslations() {
    if (i18nInstance.language === 'de') {
        countries.registerLocale(require('i18n-iso-countries/langs/de.json'));
    }
    if (i18nInstance.language === 'en') {
        countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
    }
    if (i18nInstance.language === 'fr') {
        countries.registerLocale(require('i18n-iso-countries/langs/fr.json'));
    }
    if (i18nInstance.language === 'it') {
        countries.registerLocale(require('i18n-iso-countries/langs/it.json'));
    }
}

i18nInstance.on('languageChanged', () => {
    updatePageTitle();
    updateMomentI18n();
    importCountryTranslations();
});

updateMomentI18n();
importCountryTranslations();

interface II18nResources {
    [key: string]: string;
}

interface ICustomI18n extends Omit<i18n, 'addResource' | 'addResources' | 'language'> {
    addResource: (lng: 'de' | 'en' | 'fr' | 'it', key: string, value: string) => i18n;
    addResources: (lng: 'de' | 'en' | 'fr' | 'it', resources: II18nResources) => i18n;
    language: string;
}

export default {
    ...i18nInstance,
    addResource: (lng: 'de' | 'en' | 'fr' | 'it', key: string, value: string) => i18nInstance.addResource(lng, 'translation', key, value),
    addResources: (lng: 'de' | 'en' | 'fr' | 'it', resources: II18nResources) => i18nInstance.addResources(lng, 'translation', resources),
    // TODO: this should not be necessary since we wait for the initialization of i18next to finish before rendering the portal
    // still, the language turns out to be undefined for a while, so we force-initialize it here
    language: initLanguage,
} as ICustomI18n;
